/** Standard-Gamepad (W3C): Xbox, PlayStation und die meisten Legion-Go-Pads. */

export const DEADZONE = 0.45;
export const REPEAT_INITIAL_MS = 280;
export const REPEAT_INTERVAL_MS = 140;

export const BUTTON = {
  a: 0,
  b: 1,
  x: 2,
  y: 3,
  lb: 4,
  rb: 5,
  lt: 6,
  rt: 7,
  back: 8,
  start: 9,
  l3: 10,
  r3: 11,
  up: 12,
  down: 13,
  left: 14,
  right: 15,
} as const;

export type Direction = 'up' | 'down' | 'left' | 'right';

export type PadSnapshot = {
  index: number;
  id: string;
  mapping: string;
  buttons: boolean[];
  axes: number[];
};

export type GamepadFrame = {
  pad: PadSnapshot | null;
  edges: number[];
  direction: Direction | null;
  move: boolean;
  scrollX: number;
  scrollY: number;
};

export type PollState = {
  padIndex: number | null;
  buttons: boolean[];
  repeatDirection: Direction | null;
  heldSince: number | null;
  lastMove: number | null;
  primed: boolean;
};

export type RectLike = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type SpatialCandidate = {
  id: string;
  rect: RectLike;
  containsCurrent: boolean;
  insideCurrent: boolean;
};

type PadLike = {
  id?: string;
  index?: number;
  mapping?: string;
  connected?: boolean;
  buttons?: ArrayLike<{ pressed?: boolean; value?: number } | boolean>;
  axes?: ArrayLike<number>;
};

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[role="button"]:not([aria-disabled="true"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function initialPollState(): PollState {
  return {
    padIndex: null,
    buttons: [],
    repeatDirection: null,
    heldSince: null,
    lastMove: null,
    primed: false,
  };
}

export function applyDeadzone(value: number, deadzone = DEADZONE): number {
  if (!Number.isFinite(value)) return 0;
  const magnitude = Math.abs(value);
  if (magnitude < deadzone) return 0;
  const scaled = (magnitude - deadzone) / (1 - deadzone);
  return Math.sign(value) * Math.min(1, scaled);
}

export function directionFromAxes(x: number, y: number, deadzone = DEADZONE): Direction | null {
  const ax = applyDeadzone(x, deadzone);
  const ay = applyDeadzone(y, deadzone);
  if (ax === 0 && ay === 0) return null;
  if (Math.abs(ax) >= Math.abs(ay)) return ax > 0 ? 'right' : 'left';
  return ay > 0 ? 'down' : 'up';
}

export function directionFromPad(axes: ArrayLike<number>, buttons: boolean[], deadzone = DEADZONE): Direction | null {
  const vertical = buttons[BUTTON.up] ? 'up' : buttons[BUTTON.down] ? 'down' : null;
  const horizontal = buttons[BUTTON.left] ? 'left' : buttons[BUTTON.right] ? 'right' : null;
  if (vertical && horizontal) return vertical;
  if (vertical || horizontal) return vertical || horizontal;
  return directionFromAxes(axes[0] ?? 0, axes[1] ?? 0, deadzone);
}

export function scrollFromAxes(x: number, y: number, deadzone = DEADZONE): { x: number; y: number } {
  return {
    x: applyDeadzone(x, deadzone),
    y: applyDeadzone(y, deadzone),
  };
}

export type RepeatState = {
  direction: Direction | null;
  heldSince: number | null;
  lastMove: number | null;
};

export function stepRepeat(
  state: RepeatState,
  direction: Direction | null,
  now: number,
  initialMs = REPEAT_INITIAL_MS,
  intervalMs = REPEAT_INTERVAL_MS,
): { state: RepeatState; fire: boolean } {
  if (!direction) {
    return { state: { direction: null, heldSince: null, lastMove: null }, fire: false };
  }
  if (state.direction !== direction || state.heldSince == null || state.lastMove == null) {
    return { state: { direction, heldSince: now, lastMove: now }, fire: true };
  }
  if (now - state.heldSince < initialMs) {
    return { state, fire: false };
  }
  if (now - state.lastMove >= intervalMs) {
    return { state: { ...state, lastMove: now }, fire: true };
  }
  return { state, fire: false };
}

export function pressedEdges(previous: boolean[], current: boolean[]): number[] {
  const edges: number[] = [];
  const count = Math.max(previous.length, current.length);
  for (let index = 0; index < count; index += 1) {
    if (current[index] && !previous[index]) edges.push(index);
  }
  return edges;
}

export function isStandardPad(pad: { mapping?: string; buttons?: ArrayLike<unknown>; axes?: ArrayLike<number> }): boolean {
  if (pad.mapping === 'standard') return true;
  const buttonCount = pad.buttons?.length ?? 0;
  const axisCount = pad.axes?.length ?? 0;
  return (pad.mapping === '' || pad.mapping == null) && buttonCount >= 16 && axisCount >= 4;
}

function buttonDown(button: { pressed?: boolean; value?: number } | boolean | undefined): boolean {
  if (!button) return false;
  if (typeof button === 'boolean') return button;
  return Boolean(button.pressed) || (button.value ?? 0) >= 0.5;
}

export function snapshotPad(pad: PadLike, index = pad.index ?? 0): PadSnapshot | null {
  if (pad.connected === false || !isStandardPad(pad)) return null;
  const buttons = pad.buttons ?? [];
  const axes = pad.axes ?? [];
  const pressed: boolean[] = [];
  for (let i = 0; i < buttons.length; i += 1) pressed.push(buttonDown(buttons[i]));
  const axisValues: number[] = [];
  for (let i = 0; i < axes.length; i += 1) {
    const value = Number(axes[i]);
    axisValues.push(Number.isFinite(value) ? value : 0);
  }
  return {
    index,
    id: pad.id || `pad-${index}`,
    mapping: pad.mapping || '',
    buttons: pressed,
    axes: axisValues,
  };
}

function readPadList(source: ArrayLike<PadLike | null> | null | undefined): PadSnapshot[] {
  if (!source) return [];
  const pads: PadSnapshot[] = [];
  for (let index = 0; index < source.length; index += 1) {
    const pad = source[index];
    if (!pad) continue;
    const snap = snapshotPad(pad, pad.index ?? index);
    if (snap) pads.push(snap);
  }
  return pads;
}

function pickPad(state: PollState, pads: PadSnapshot[]): PadSnapshot | null {
  if (pads.length === 0) return null;
  if (state.padIndex != null) {
    const current = pads.find((pad) => pad.index === state.padIndex);
    if (current) return current;
  }
  return pads[0];
}

export function pollGamepad(
  state: PollState,
  getGamepads: () => ArrayLike<PadLike | null> | null,
  now: number,
): { state: PollState; frame: GamepadFrame } {
  let raw: ArrayLike<PadLike | null> | null = null;
  try {
    raw = getGamepads();
  } catch {
    raw = null;
  }
  const pad = pickPad(state, readPadList(raw));
  const empty: GamepadFrame = { pad: null, edges: [], direction: null, move: false, scrollX: 0, scrollY: 0 };
  if (!pad) return { state: initialPollState(), frame: empty };
  if (!state.primed || state.padIndex !== pad.index) {
    return {
      state: {
        padIndex: pad.index,
        buttons: pad.buttons.slice(),
        repeatDirection: null,
        heldSince: null,
        lastMove: null,
        primed: true,
      },
      frame: { ...empty, pad },
    };
  }
  const edges = pressedEdges(state.buttons, pad.buttons);
  const direction = directionFromPad(pad.axes, pad.buttons);
  const repeat = stepRepeat(
    { direction: state.repeatDirection, heldSince: state.heldSince, lastMove: state.lastMove },
    direction,
    now,
  );
  const scroll = scrollFromAxes(pad.axes[2] ?? 0, pad.axes[3] ?? 0);
  return {
    state: {
      padIndex: pad.index,
      buttons: pad.buttons.slice(),
      repeatDirection: repeat.state.direction,
      heldSince: repeat.state.heldSince,
      lastMove: repeat.state.lastMove,
      primed: true,
    },
    frame: {
      pad,
      edges,
      direction,
      move: repeat.fire,
      scrollX: scroll.x,
      scrollY: scroll.y,
    },
  };
}

function centerOf(rect: RectLike) {
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

function axisDelta(from: RectLike, to: RectLike, direction: Direction) {
  const start = centerOf(from);
  const end = centerOf(to);
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (direction === 'left') return { primary: -dx, secondary: dy };
  if (direction === 'right') return { primary: dx, secondary: dy };
  if (direction === 'up') return { primary: -dy, secondary: dx };
  return { primary: dy, secondary: dx };
}

export function isInDirection(from: RectLike, to: RectLike, direction: Direction): boolean {
  const { primary, secondary } = axisDelta(from, to, direction);
  if (primary <= 8) return false;
  return Math.abs(secondary) <= primary * 1.75 + 48;
}

function spatialScore(from: RectLike, to: RectLike, direction: Direction): number {
  const { primary, secondary } = axisDelta(from, to, direction);
  return primary + Math.abs(secondary) * 1.25;
}

export function chooseSpatialTarget(
  current: RectLike,
  candidates: SpatialCandidate[],
  direction: Direction,
): string | null {
  const bestOf = (list: SpatialCandidate[]): string | null => {
    let bestId: string | null = null;
    let bestScore = Infinity;
    for (const candidate of list) {
      if (!isInDirection(current, candidate.rect, direction)) continue;
      const score = spatialScore(current, candidate.rect, direction);
      if (score < bestScore - 0.01) {
        bestScore = score;
        bestId = candidate.id;
      }
    }
    return bestId;
  };

  const children = candidates.filter((candidate) => candidate.insideCurrent && !candidate.containsCurrent);
  const childHit = bestOf(children);
  if (childHit) return childHit;

  const outside = candidates.filter((candidate) => !candidate.insideCurrent && !candidate.containsCurrent);
  const outsideHit = bestOf(outside);
  if (outsideHit) return outsideHit;

  return bestOf(candidates.filter((candidate) => candidate.containsCurrent));
}

export function isGamepadFocusable(element: HTMLElement): boolean {
  const group = element.hasAttribute('data-gamepad-zone') || element.hasAttribute('data-gamepad-tile');
  if (!element.isConnected || (!group && !element.matches(FOCUSABLE_SELECTOR))) return false;
  if (element.closest('[data-gamepad-ignore]')) return false;
  if (element.hasAttribute('disabled') || element.getAttribute('aria-disabled') === 'true') return false;
  if (element.closest('[hidden], [aria-hidden="true"]')) return false;
  const tabIndex = element.getAttribute('tabindex');
  if (!group && tabIndex === '-1') return false;
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  if (style.opacity !== '' && Number(style.opacity) === 0) return false;
  const rect = element.getBoundingClientRect();
  return rect.width >= 1 || rect.height >= 1;
}

export function listFocusables(scope: ParentNode): HTMLElement[] {
  const nodes = scope.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  const found: HTMLElement[] = [];
  nodes.forEach((node) => {
    if (isGamepadFocusable(node)) found.push(node);
  });
  return found;
}

function isInteractive(element: HTMLElement): boolean {
  return element.matches('a[href], button, input, select, textarea, [role="button"]');
}

/** A activates the focused control. A catalog item with its own action button uses that button. */
export function activationTarget(focused: HTMLElement): HTMLElement | null {
  if (!isGamepadFocusable(focused)) return null;
  const item = focused.closest('[data-gamepad-item]');
  if (item instanceof HTMLElement) {
    const primary = item.querySelector<HTMLElement>('[data-gamepad-primary]');
    if (
      primary
      && isGamepadFocusable(primary)
      && (focused === item || focused === primary || !isInteractive(focused))
    ) {
      return primary;
    }
  }
  return focused;
}

type FrameListener = (frame: GamepadFrame) => void;

let listeners = new Set<FrameListener>();
let rafId = 0;
let pollState = initialPollState();

function frameLoop(now: number) {
  if (listeners.size === 0) {
    rafId = 0;
    return;
  }
  rafId = window.requestAnimationFrame(frameLoop);
  const sampled = pollGamepad(
    pollState,
    () => (typeof navigator !== 'undefined' && navigator.getGamepads ? navigator.getGamepads() : []),
    now,
  );
  pollState = sampled.state;
  listeners.forEach((listener) => {
    listener(sampled.frame);
  });
}

export function subscribeGamepad(listener: FrameListener): () => void {
  listeners.add(listener);
  if (listeners.size === 1) {
    pollState = initialPollState();
    rafId = window.requestAnimationFrame(frameLoop);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && rafId) {
      window.cancelAnimationFrame(rafId);
      rafId = 0;
      pollState = initialPollState();
    }
  };
}

export function gamepadListenerCount(): number {
  return listeners.size;
}
